import { useEffect } from 'react';

/**
 * Strip dangerous CSS patterns that could be used for code execution
 * or external resource loading. Keeps legitimate styling intact.
 */
function sanitizeCss(css: string): string {
  return css
    .replace(/@import\b[^;]*;?/gi, '/* @import removed */')
    .replace(/expression\s*\(/gi, '/* expression( removed */')
    .replace(/javascript\s*:/gi, '/* javascript: removed */');
}

interface UseEmbedStylesParams {
  headerFont: string;
  headerFontWeight: string;
  bodyFont: string;
  bodyFontWeight: string;
  customCss: string;
  /** Unique suffix to avoid ID collisions when chat + search coexist */
  scope: string;
}

/**
 * Shared hook for embed pages — loads Google Fonts and injects sanitized custom CSS.
 */
export function useEmbedStyles({
  headerFont,
  headerFontWeight,
  bodyFont,
  bodyFontWeight,
  customCss,
  scope,
}: UseEmbedStylesParams): void {
  useEffect(() => {
    const entries: [string, string][] = (
      [
        [headerFont, headerFontWeight],
        [bodyFont, bodyFontWeight],
      ] as [string, string][]
    ).filter(([f]) => f);
    if (entries.length === 0) return;
    const families = entries
      .map(([f, w]) => {
        const name = f.replace(/ /g, '+');
        return w ? `${name}:wght@${w}` : name;
      })
      .join('&family=');
    const linkId = `google-fonts-embed-${scope}`;
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
  }, [headerFont, headerFontWeight, bodyFont, bodyFontWeight, scope]);

  useEffect(() => {
    const styleId = `embed-custom-css-${scope}`;
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!customCss) {
      style?.remove();
      return;
    }
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = sanitizeCss(customCss);
  }, [customCss, scope]);
}
